/**
 * The JOIN screen's body, shared by both trees (3026, docs/INVITES.md).
 *
 * NOT PORTED FROM A MIRROR, because there is no mirror to port. The design
 * prototype has no invitation flow at all — its accounts cannot outlive their
 * crew and its invite is a temp password handed over by hand. This screen is
 * built from Marcus's own words (2026-08-20) and owes a DESIGN-CHANGELOG entry
 * upstream; the type scale, colours and caps idiom are the landing screen's, so
 * it at least reads as the same product.
 *
 * The chrome differs between trees — the phone gets a full-bleed column, the
 * desktop a centred card — so, like `OwnerConfirm`, the questions live here
 * once and each tree wraps them.
 */

import type { CSSProperties } from 'react';

import { useQd } from '../store/index.ts';
import { joinVM } from '../store/selectors.ts';
import { PixelButton } from './PixelButton.tsx';

const MONO = 'var(--gt-font-mono)';

export interface JoinMetrics {
  headFs: string;
  bodyFs: string;
  fieldFs: string;
  btnFs: number | string;
  pad: string;
}

const FIELD = (m: JoinMetrics): CSSProperties => ({
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: m.fieldFs,
  padding: '12px 11px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

export function JoinCard({ m }: { m: JoinMetrics }): JSX.Element {
  const s = useQd();
  const v = joinVM(s);

  if (v.loading) {
    return (
      <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted)', padding: m.pad }}>
        READING YOUR INVITE…
      </div>
    );
  }

  // Every closed state: expired, revoked, superseded, accepted, declined, or a
  // token that never existed. None of them name the crew or the inviter except
  // "already in", which the reader demonstrably knows already.
  if (!v.live) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: m.pad }}>
        <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
          {v.closedTxt}
        </div>
        {/* An already-used invitation gets its own second line rather than
            the generic one. Whoever is reading it either IS in the crew and
            should sign in, or was removed and needs a fresh invitation — the
            screen cannot tell which, and must not claim to. */}
        {v.state === 'declined' ? null : (
          <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', lineHeight: 1.8 }}>
            {v.state === 'accepted'
              ? 'SIGN IN, OR ASK FOR A NEW INVITE IF YOU ARE NO LONGER IN THE PARTY.'
              : 'ASK WHOEVER INVITED YOU TO SEND ANOTHER ONE.'}
          </div>
        )}
        <PixelButton
          label="LOG IN ▸"
          variant="outline"
          onClick={() => s.nav('login')}
          fs={m.btnFs}
          pad="15px 14px"
          center
        />
      </div>
    );
  }

  /* The session died under the screen. NOT the same as being signed out, and
     it must not be drawn like it: whoever is reading this had a session a
     moment ago, so their account exists and the one thing they need is a way
     back into it. No name field, no password field, no "create an account" —
     just the door, and a promise that they land back here. */
  if (v.needsLogin) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: m.pad }}>
        <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
          {v.needsLoginTxt}
        </div>
        <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', lineHeight: 1.9 }}>
          {v.needsLoginHint}
        </div>
        <PixelButton
          label="LOG IN ▸"
          onClick={() => s.nav('login')}
          fs={m.btnFs}
          pad="16px 14px"
          center
        />
      </div>
    );
  }

  // Signed in as the wrong person. Common enough to deserve a door rather than
  // a refusal: an owner testing their own invitation hits this every time.
  if (v.wrongAccount) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: m.pad }}>
        <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
          {v.wrongAccountTxt}
        </div>
        <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted)', lineHeight: 1.9 }}>
          {v.sentToTxt} — {v.wrongAccountHint}
        </div>
        <PixelButton
          label="SIGN OUT ▸"
          variant="outline"
          tone="acc2"
          onClick={() => void s.signOut()}
          fs={m.btnFs}
          pad="15px 14px"
          center
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13, padding: m.pad }}>
      <div style={{ fontFamily: MONO, fontSize: m.headFs, color: 'var(--gt-text)', lineHeight: 1.6 }}>
        {v.headline}
      </div>
      <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)', letterSpacing: '.06em' }}>
        {v.sentToTxt}
      </div>

      {/* Signed out: they choose BOTH of these. Nobody has picked a name for
          them, and there is no temp password to be handed. */}
      {v.needsAccount ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}>
              YOUR NAME
            </span>
            <input
              value={v.joinName}
              onChange={(e) => s.setJoinForm({ joinName: e.target.value })}
              maxLength={24}
              placeholder="ROBIN"
              style={FIELD(m)}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}>
              PASSWORD
            </span>
            <input
              type="password"
              value={v.joinPw}
              onChange={(e) => s.setJoinForm({ joinPw: e.target.value })}
              style={FIELD(m)}
            />
            <span style={{ fontFamily: MONO, fontSize: '9px', letterSpacing: '.05em', color: 'var(--gt-muted-2)' }}>
              {v.pwHint}
            </span>
          </div>

          {/* TYPED AGAIN, and this is the one screen in the app where it is not
              optional. Every other password field belongs to somebody already
              signed in, who can try again. This is the only credential a brand
              new account will ever have, and the invitation is spent the moment
              they tap JOIN — a typo here leaves them locked out of an account
              that exists, with no session, no old password to change it with,
              and no reset to send. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}>
              PASSWORD AGAIN
            </span>
            <input
              type="password"
              value={v.joinPw2}
              onChange={(e) => s.setJoinForm({ joinPw2: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.repeat) void s.acceptJoin();
              }}
              style={{
                ...FIELD(m),
                // Says so as they type, rather than after the tap that spends
                // the invitation.
                ...(v.pwMismatch ? { borderColor: 'var(--acc2)' } : null),
              }}
            />
            {v.pwMismatch ? (
              <span style={{ fontFamily: MONO, fontSize: '9px', letterSpacing: '.05em', color: 'var(--acc2)' }}>
                THESE DO NOT MATCH
              </span>
            ) : null}
          </div>
        </>
      ) : null}

      {v.hasErr ? (
        <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--acc2)', lineHeight: 1.8 }}>
          ✕ {v.joinErr}
        </div>
      ) : null}

      <PixelButton
        label={v.joinLbl}
        busy={v.joinBusy}
        onClick={() => void s.acceptJoin()}
        fs={m.btnFs}
        pad="16px 14px"
        center
      />
      <PixelButton
        label="NO THANKS"
        variant="outline"
        busy={v.joinBusy}
        onClick={() => void s.declineJoin()}
        fs={m.btnFs}
        pad="14px 14px"
        center
        shadow={false}
      />

      {v.needsAccount ? (
        <div
          onClick={() => s.nav('login')}
          style={{
            fontFamily: MONO,
            fontSize: '9px',
            letterSpacing: '.05em',
            color: 'var(--gt-muted)',
            textAlign: 'center',
            cursor: 'pointer',
            paddingTop: 4,
          }}
        >
          {v.haveAccountTxt}
        </div>
      ) : null}
    </div>
  );
}
